import { $, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { fillForm } from '../lib/form.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { findSelectMenu } from '../lib/select-menu.js';

/**
 * The auto-response editor.
 *
 * Triggers and responses are entered one per line rather than as repeatable
 * rows: people write twenty-five in a row, and twenty-five "add" buttons to
 * click would have them give up by the third.
 */

interface AutoResponseRule {
  _id: string;
  name: string;
  enabled: boolean;
  type: string;
  triggers: string[];
  caseSensitive: boolean;
  responses: string[];
  templateName: string;
  reactions: string[];
  suppressMentions: boolean;
  deleteAfterSeconds: number;
  deleteTrigger: boolean;
  allowBots: boolean;
  allowSystem: boolean;
  allowedChannelIds: string[];
  blockedChannelIds: string[];
  allowedRoleIds: string[];
  blockedRoleIds: string[];
  guildCooldownSeconds: number;
  userCooldownSeconds: number;
  uses: number;
}

interface EditorPageData {
  rule: AutoResponseRule;
  guildId: string;
}

export function initAutoresponderEditorPage(): void {
  const form = $<HTMLFormElement>('[data-rule-form]');
  if (!form) return;

  const { rule, guildId } = pageData<EditorPageData>();

  fillForm(form, rule as unknown as Record<string, unknown>);

  // The lists are not fields: `fillForm` would write "a,b" where the page
  // expects one line per entry.
  setText(form, 'triggers', rule.triggers.join('\n'));
  setText(form, 'responses', rule.responses.join('\n'));
  setText(form, 'reactions', rule.reactions.join(' '));

  for (const field of [
    'allowedChannelIds',
    'blockedChannelIds',
    'allowedRoleIds',
    'blockedRoleIds',
  ] as const) {
    const menu = findSelectMenu(form, field);
    if (menu) menu.value = rule[field];
  }

  // Boxes ticked by default in the template have to follow the saved state,
  // including when that state is false.
  for (const flag of [
    'enabled',
    'caseSensitive',
    'suppressMentions',
    'deleteTrigger',
    'allowBots',
    'allowSystem',
  ] as const) {
    const node = form.elements.namedItem(flag);
    if (node instanceof HTMLInputElement) node.checked = rule[flag];
  }

  mountResourceEditor({
    endpoint: 'autoresponder',
    listUrl: `/modules/autoresponder?guildId=${guildId}`,
    guildId,
    form,
    itemId: rule._id,

    serialize: () => ({
      name: text(form, 'name'),
      enabled: checked(form, 'enabled'),

      type: choice(form, 'type', 'keyword'),
      triggers: lines(form, 'triggers'),
      caseSensitive: checked(form, 'caseSensitive'),

      responses: lines(form, 'responses'),
      templateName: text(form, 'templateName'),
      reactions: words(form, 'reactions'),

      suppressMentions: checked(form, 'suppressMentions'),
      deleteAfterSeconds: number(form, 'deleteAfterSeconds'),
      deleteTrigger: checked(form, 'deleteTrigger'),

      allowBots: checked(form, 'allowBots'),
      allowSystem: checked(form, 'allowSystem'),

      allowedChannelIds: findSelectMenu(form, 'allowedChannelIds')?.value ?? [],
      blockedChannelIds: findSelectMenu(form, 'blockedChannelIds')?.value ?? [],
      allowedRoleIds: findSelectMenu(form, 'allowedRoleIds')?.value ?? [],
      blockedRoleIds: findSelectMenu(form, 'blockedRoleIds')?.value ?? [],

      guildCooldownSeconds: number(form, 'guildCooldownSeconds'),
      userCooldownSeconds: number(form, 'userCooldownSeconds'),
      publish: false,
    }),

    labels: {
      saved: ct('ruleSaved', 'Auto response saved.'),
      confirmDelete: ct('confirmDeleteRule', 'Delete this auto response?'),
      deleted: ct('ruleDeleted', 'Auto response deleted.'),
    },
  });
}

/* ------------------------------------------------------------------- champs */

/**
 * A field's value, whatever kind of control carries it.
 *
 * `<select>` is in the list because the template picker is one — native on
 * purpose, so `upgradeSelects` leaves its options where the server put them.
 */
function text(form: HTMLFormElement, name: string): string {
  const node = form.elements.namedItem(name);
  if (node instanceof HTMLSelectElement) return node.value;

  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
    ? node.value.trim()
    : '';
}

function setText(form: HTMLFormElement, name: string, next: string): void {
  const node = form.elements.namedItem(name);
  if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) node.value = next;
}

/** A multi-line input, reduced to its non-empty lines. */
function lines(form: HTMLFormElement, name: string): string[] {
  return text(form, name)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

/** The reactions, separated by spaces rather than by newlines. */
function words(form: HTMLFormElement, name: string): string[] {
  return text(form, name).split(/\s+/).filter(Boolean);
}

function number(form: HTMLFormElement, name: string): number {
  const node = form.elements.namedItem(name);
  const parsed = node instanceof HTMLInputElement ? Number(node.value) : 0;
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function checked(form: HTMLFormElement, name: string): boolean {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement ? node.checked : false;
}

/** The checked value of a card group, read through its wrapper. */
function choice(form: HTMLFormElement, name: string, fallback: string): string {
  const wrapper = form.querySelector(`[data-field="${CSS.escape(name)}"]`);
  const ticked = wrapper?.querySelector<HTMLInputElement>('input[type="radio"]:checked');
  return ticked?.value || fallback;
}
